import React from 'react';
import { Paper, makeStyles, Theme, Checkbox } from '@material-ui/core';
import { ListDecoratedMedisanteDevice, DevicesSortBy } from '../../types';
import { mobileSortOptions } from '../../config';
import MedSelect from '../../../../eliot-components/src/Common/MedSelect';
import { SortOptionSelectItem, SortOption } from '../../../../utils/sort';
import { useAuthUser } from '../../../../contexts/AuthProvider/hooks';
import SelectableDeviceBox from '../SelectableDeviceBox/SelectableDeviceBox';
import { useInfinitePaginationWithRoot } from '../DevicesTable/hooks';
import InfiniteScroll from 'react-infinite-scroller';
import { useSnackbar } from 'notistack';

const useStyles = makeStyles((theme: Theme) => ({
  device: {
    margin: theme.spacing(0, -1, 1, -1),
    padding: theme.spacing(1),
    userSelect: 'none',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 170,
    flexShrink: 0,
  },
  selected: {
    background: theme.palette.action.selected,
  },
  selectAll: {
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    margin: theme.spacing(1, -1),
    '& .label': {
      flexGrow: 1,
    },
  },
  fvb: {
    display: 'flex',
    flex: '0 0 40%',
    marginRight: '5%',
    whiteSpace: 'nowrap',
    marginTop: theme.spacing(0.5),
    alignItems: 'center',
  },

  field: {
    display: 'inline-block',
    backgroundColor: theme.palette.action.hover,
    minWidth: '50px',
    margin: theme.spacing(0, 1, 0, 0),
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    textAlign: 'left',
  },
  value: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  valueLong: {
    minWidth: '220px',
  },

  valueShort: {
    minWidth: '100px',
  },
  health: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  icons: {
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(0.5),
    justifyContent: 'center',
  },
  render: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  status: {
    display: 'inline-flex',
    alignItems: 'center',
    '& .current': {
      marginRight: 5,
    },
  },
}));

interface Props {
  selectedDeviceIds: string[];
  devices: ListDecoratedMedisanteDevice[];
  sortOptionId: string | undefined;
  updateSortState: React.Dispatch<SortOption<DevicesSortBy>>;
  onSelectionChanged: (deviceId: string) => void;
  selectAll: { all: boolean; some: boolean };
  onSelectAllChanged: () => void;
  totalView: boolean;
}

const MobileLayout = (props: Props) => {
  const {
    devices,
    selectedDeviceIds,
    onSelectionChanged,
    selectAll,
    onSelectAllChanged,
    updateSortState,
    sortOptionId,
    totalView,
  } = props;
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const { authUser } = useAuthUser();
  const someAreSelected = devices.some(
    (d) => selectedDeviceIds.indexOf(d.id) !== -1
  );
  const shouldAllowDeviceSelect = authUser.selectedOrgs.length <= 1;

  const [data, nextPage, hasMore] = useInfinitePaginationWithRoot(devices);
  const setSnack = (snack: string) => {
    if (!!snack) {
      enqueueSnackbar(snack, { variant: 'info' });
    }
  };

  if (devices && devices.length > 0) {
    const view = (
      <InfiniteScroll
        element="div"
        pageStart={0}
        initialLoad={false}
        loadMore={nextPage}
        hasMore={hasMore}
        threshold={270}
      >
        {data.map((item) => {
          const css = [classes.device];
          if (selectedDeviceIds.indexOf(item.id) !== -1) {
            css.push(classes.selected);
          }
          const shouldDisableCheckbox =
            someAreSelected &&
            !(selectedDeviceIds.indexOf(item.id) !== -1) &&
            authUser.selectedOrgs.length <= 1;

          return (
            <SelectableDeviceBox
              totalView={totalView}
              key={item.id}
              isSelected={selectedDeviceIds.indexOf(item.id) !== -1}
              device={item}
              disableSelect={shouldDisableCheckbox}
              onSelectionChanged={onSelectionChanged}
              onClipboardClick={(txt) => {
                if (navigator && navigator.clipboard) {
                  navigator.clipboard.writeText(txt);
                  setSnack(`${txt} copied to clipboard`);
                }
              }}
            />
          );
        })}
      </InfiniteScroll>
    );

    return (
      <>
        <MedSelect
          name="sort-option"
          placeholder="Sort by"
          variant="outlined"
          fullWidth
          options={mobileSortOptions}
          valueGetter={(o: SortOptionSelectItem<DevicesSortBy>) => o.id}
          optionTextGetter={(o: SortOptionSelectItem<DevicesSortBy>) => o.label}
          value={sortOptionId}
          onChange={(e) => {
            const option = mobileSortOptions.find(
              (o) => o.id === e.target.value
            );
            option && updateSortState(option.value);
          }}
        />
        {shouldAllowDeviceSelect ? (
          <Paper className={classes.selectAll}>
            <span className="label">Select all</span>{' '}
            <Checkbox
              indeterminate={selectAll.some}
              checked={selectAll.all}
              onChange={onSelectAllChanged}
            />
          </Paper>
        ) : null}
        {view}
      </>
    );
  }

  return null;
};

export default MobileLayout;
